/**
 * Detalle de un manifiesto: avance, activos y eliminados.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { Button } from '../../components/ui'
import { carrierLabel } from '../../config/manifiestos/manifiestosCarriersConfig'
import {
  ManifiestoApiError,
  getManifiesto,
} from '../../services/manifiestos/manifiestosService'
import {
  MANIFIESTO_ESTADO_IMPRESO,
  MANIFIESTO_ESTADO_LISTO,
  MANIFIESTO_LINEA_ACTIVA,
  type Manifiesto,
  type ManifiestoPedidoLinea,
  type ManifiestoUsuario,
} from '../../types/manifiestos'
import { imprimirManifiestoPdf } from '../../utils/manifiestos/imprimirManifiesto'

const PASOS = ['Creado', 'En verificación', 'Listo para imprimir', 'Impreso', 'Cerrado']

const ESTADO_CLASS: Record<string, string> = {
  Creado: 'bg-gray-100 text-gray-600',
  'En verificación': 'bg-amber-100 text-amber-700',
  'Listo para imprimir': 'bg-blue-100 text-blue-700',
  Impreso: 'bg-purple-100 text-purple-700',
  Cerrado: 'bg-green-100 text-green-700',
}

function formatFecha(value?: string | null): string {
  if (!value) return '—'
  const normalized = value.includes('T') ? value : value.replace(' ', 'T')
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function nombre(user?: ManifiestoUsuario | null): string {
  if (!user) return '—'
  const full = `${user.nombre} ${user.apellido}`.trim()
  return full || '—'
}

function esActivo(linea: ManifiestoPedidoLinea): boolean {
  return String(linea.estado || MANIFIESTO_LINEA_ACTIVA) === MANIFIESTO_LINEA_ACTIVA
}

function completo(linea: ManifiestoPedidoLinea): boolean {
  const total = Number(linea.unidades_empaque || 0)
  const scanned = Number(linea.paquetes_verificados || 0)
  return total > 0 && scanned >= total
}

const ManifiestoDetalle: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [manifiesto, setManifiesto] = useState<Manifiesto | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'activos' | 'eliminados'>('activos')
  const [printing, setPrinting] = useState(false)

  useEffect(() => {
    const manifiestoId = Number(id)
    if (!Number.isInteger(manifiestoId)) {
      setLoading(false)
      return
    }
    setLoading(true)
    void getManifiesto(manifiestoId)
      .then(setManifiesto)
      .catch((error: unknown) => {
        const code = error instanceof ManifiestoApiError ? error.code : ''
        toast.error(
          code === 'MANIFIESTO_NO_ENCONTRADO'
            ? 'No se encontró el manifiesto'
            : error instanceof Error ? error.message : 'No se pudo abrir el manifiesto',
          { position: 'top-center' }
        )
      })
      .finally(() => setLoading(false))
  }, [id])

  const activos = useMemo(
    () => (manifiesto?.pedidos || []).filter(esActivo),
    [manifiesto]
  )
  const eliminados = useMemo(
    () => (manifiesto?.pedidos || []).filter((linea) => !esActivo(linea)),
    [manifiesto]
  )
  const filas = tab === 'activos' ? activos : eliminados
  const escaneados = activos.reduce((sum, linea) => sum + Number(linea.paquetes_verificados || 0), 0)
  const cajas = activos.reduce((sum, linea) => sum + Number(linea.unidades_empaque || 0), 0)
  const completos = activos.filter(completo).length
  const pendientes = activos.length - completos
  const avance = cajas > 0 ? Math.min(100, Math.round((escaneados / cajas) * 100)) : 0
  const pasoActual = PASOS.indexOf(manifiesto?.estado || '')
  const puedeImprimir = manifiesto?.estado === MANIFIESTO_ESTADO_LISTO || manifiesto?.estado === MANIFIESTO_ESTADO_IMPRESO
  const consecutivo = manifiesto?.consecutivo || (manifiesto ? `MN-${String(manifiesto.id).padStart(5, '0')}` : '')

  const descargar = async () => {
    if (!manifiesto) return
    setPrinting(true)
    try {
      const ok = await imprimirManifiestoPdf(manifiesto.id, manifiesto.transportadora || '')
      if (ok) toast.success('PDF descargado', { position: 'top-center' })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo armar el PDF', { position: 'top-center' })
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <button type="button" className="inline-flex items-center gap-1 text-sm text-blue-700 mb-4" onClick={() => navigate('/envios/manifiestos')}>
        <ArrowLeftIcon className="h-4 w-4" />
        Volver a manifiestos
      </button>

      {loading && <p className="text-sm text-gray-500">Cargando manifiesto…</p>}

      {!loading && manifiesto && (
        <>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold text-blue-700">Manifiesto {consecutivo}</h2>
                <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${ESTADO_CLASS[manifiesto.estado] || 'bg-gray-100 text-gray-600'}`}>
                  {manifiesto.estado}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {carrierLabel(manifiesto.transportadora) || manifiesto.transportadora}
                {' · Creado el '}
                {formatFecha(manifiesto.fechaCreacion)}
                {' · por '}
                {nombre(manifiesto.creadoPor)}
              </p>
            </div>
            <Button
              variant="secondary"
              outline
              leftIcon={ArrowDownTrayIcon}
              isLoading={printing}
              disabled={!puedeImprimir}
              onClick={() => void descargar()}
            >
              Descargar PDF
            </Button>
          </div>

          <ol className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
            {PASOS.map((paso, index) => {
              const activo = index <= pasoActual
              const fecha = index === 0
                ? manifiesto.fechaCreacion
                : index === 1
                  ? manifiesto.fechaInicioVerificacion
                  : index === pasoActual
                    ? manifiesto.fechaModificacion
                    : null
              return (
                <li key={paso} className={`border-t-2 pt-2 ${activo ? 'border-blue-600' : 'border-gray-200'}`}>
                  <p className={`text-sm ${activo ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>{paso}</p>
                  <p className="text-xs text-gray-400">{activo ? formatFecha(fecha) : '—'}</p>
                </li>
              )
            })}
          </ol>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex gap-2 mb-4">
                <button type="button" className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'activos' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`} onClick={() => setTab('activos')}>
                  Activos ({activos.length})
                </button>
                <button type="button" className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'eliminados' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`} onClick={() => setTab('eliminados')}>
                  Eliminados ({eliminados.length})
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400">
                      <th className="py-2 font-medium">PEDIDO</th>
                      <th className="py-2 font-medium">PAQUETES</th>
                      <th className="py-2 font-medium">VERIFICACIÓN</th>
                      <th className="py-2 font-medium">ASIGNADO</th>
                      <th className="py-2 font-medium">ÚLTIMO ESCANEO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-6 text-gray-400">No hay pedidos en esta lista</td>
                      </tr>
                    )}
                    {filas.map((linea) => {
                      const total = Number(linea.unidades_empaque || 0)
                      const scanned = Number(linea.paquetes_verificados || 0)
                      const ratio = total > 0 ? Math.min(100, (scanned / total) * 100) : 0
                      const listo = completo(linea)
                      return (
                        <tr key={linea.id || linea.id_pedido} className="border-t border-gray-100">
                          <td className="py-3 font-medium text-gray-900"># {linea.numero_pedido || linea.id_pedido}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full ${listo ? 'bg-emerald-500' : 'bg-gray-300'}`} style={{ width: `${ratio}%` }} />
                              </div>
                              <span className="text-gray-600">{scanned}/{total}</span>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs ${listo ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                              {listo ? 'Completo' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="py-3 text-gray-500">{formatFecha(linea.fecha_asignacion)}</td>
                          <td className="py-3 text-gray-500">{formatFecha(linea.fecha_ultima_verificacion)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="space-y-4">
              <section className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="text-xs font-semibold tracking-wide text-gray-500 mb-3">AVANCE DE VERIFICACIÓN</h3>
                <p className="text-3xl font-semibold text-gray-900">
                  {escaneados}
                  <span className="text-base font-normal text-gray-400"> / {cajas} paquetes</span>
                </p>
                <div className="h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-blue-600" style={{ width: `${avance}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <div>
                    <p className="text-gray-400">Pedidos completos</p>
                    <p className="text-emerald-600 font-semibold">{completos}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Con pendientes</p>
                    <p className="text-red-600 font-semibold">{pendientes}</p>
                  </div>
                </div>
              </section>
              <section className="bg-white border border-gray-200 rounded-xl p-4 text-sm">
                <h3 className="text-xs font-semibold tracking-wide text-gray-500 mb-3">INFORMACIÓN</h3>
                <dl className="space-y-2">
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">Transportadora</dt><dd>{carrierLabel(manifiesto.transportadora) || '—'}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">Creado por</dt><dd>{nombre(manifiesto.creadoPor)}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">Fecha creación</dt><dd>{formatFecha(manifiesto.fechaCreacion)}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">Verificado por</dt><dd>{nombre(manifiesto.verificadoPor)}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">Inicio verificación</dt><dd>{formatFecha(manifiesto.fechaInicioVerificacion)}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-400">Cerrado por</dt><dd>{nombre(manifiesto.cerradoPor)}</dd></div>
                </dl>
              </section>
            </aside>
          </div>
        </>
      )}
    </div>
  )
}

export default ManifiestoDetalle
